/**
 * Global toast queue. Call `toast.success('Gespeichert')` from anywhere (also outside React);
 * a single <Toaster /> near the app root renders the queue.
 */
import { UNSTABLE_ToastQueue as ToastQueue } from 'react-aria-components';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastAction {
  label: string;
  onAction: () => void;
}

export interface ToastContent {
  tone: ToastTone;
  title: string;
  description?: string;
  action?: ToastAction;
}

/** Long enough to read; React Aria recommends at least 5 s. */
export const DEFAULT_TIMEOUT_MS = 5000;
export const MAX_VISIBLE_TOASTS = 3;

export function createToastQueue(): ToastQueue<ToastContent> {
  return new ToastQueue<ToastContent>({ maxVisibleToasts: MAX_VISIBLE_TOASTS });
}

export const toastQueue = createToastQueue();

export interface ShowToastOptions {
  description?: string;
  action?: ToastAction;
  /**
   * Auto-dismiss after this many ms; `null` keeps the toast until it is closed.
   * Defaults: errors and toasts with an action stay (users need time to act), the rest close after 5 s.
   */
  timeout?: number | null;
  queue?: ToastQueue<ToastContent>;
}

function show(tone: ToastTone, title: string, options: ShowToastOptions = {}): string {
  const { timeout, queue = toastQueue, ...content } = options;
  const persistent = tone === 'danger' || content.action !== undefined;
  const effectiveTimeout = timeout === undefined ? (persistent ? null : DEFAULT_TIMEOUT_MS) : timeout;
  return queue.add(
    { tone, title, ...content },
    effectiveTimeout === null ? {} : { timeout: effectiveTimeout },
  );
}

export const toast = {
  info: (title: string, options?: ShowToastOptions) => show('info', title, options),
  success: (title: string, options?: ShowToastOptions) => show('success', title, options),
  warning: (title: string, options?: ShowToastOptions) => show('warning', title, options),
  danger: (title: string, options?: ShowToastOptions) => show('danger', title, options),
  close: (key: string, queue: ToastQueue<ToastContent> = toastQueue) => {
    queue.close(key);
  },
};
